
"use client";

import type { User } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import type { PropsWithChildren} from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { usePathname, useRouter } from 'next/navigation';
import type { UserProfile } from '@/types/user';

// ── Types ─────────────────────────────────────────────────────────────────────

/** All possible account types that can be impersonated. */
export const VIEW_AS_OPTIONS = [
  { value: 'creator',          label: 'Artist / Creator',  emoji: '🎤' },
  { value: 'consumer',         label: 'Fan / Listener',    emoji: '🎧' },
  { value: 'venue',            label: 'Venue / Event',     emoji: '🏟' },
  { value: 'consumer-business',label: 'Business',          emoji: '💼' },
  { value: 'admin',            label: 'Admin',             emoji: '🛡' },
] as const;

export type ViewAsValue = typeof VIEW_AS_OPTIONS[number]['value'];

interface AuthContextType {
  user: User | null;
  /** Effective profile — accountType overridden when admin is using "View As" */
  userProfile: UserProfile | null;
  /** Always the real Firestore profile, regardless of View As mode */
  realUserProfile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Current impersonated account type, or null */
  viewAs: ViewAsValue | null;
  /** Set to a ViewAsValue to impersonate, null to exit. Only works for super admins. */
  setViewAs: (type: ViewAsValue | null) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [realUserProfile, setRealUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  // Keep a ref so snapshot callbacks can read the latest pathname without
  // being in the effect dependency array (which would cause re-subscription on every nav).
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // View-as impersonation state (session-scoped so it resets on tab close).
  // Storage access wrapped — on iOS with restricted storage, a bare
  // sessionStorage read/write throws, and here that would break auth boot.
  const [viewAs, setViewAsState] = useState<ViewAsValue | null>(() => {
    if (typeof window === 'undefined') return null;
    try { return (sessionStorage.getItem('bb-view-as') as ViewAsValue) || null; } catch { return null; }
  });

  const setViewAs = (type: ViewAsValue | null) => {
    // Guard: only super admins may impersonate
    if (type !== null && realUserProfile?.accountType?.toLowerCase() !== 'superadmin') return;
    setViewAsState(type);
    try {
      if (type) sessionStorage.setItem('bb-view-as', type);
      else sessionStorage.removeItem('bb-view-as');
    } catch { /* ignore */ }
  };

  // Derive the effective profile — override accountType when impersonating
  const userProfile: UserProfile | null =
    viewAs && realUserProfile
      ? { ...realUserProfile, accountType: viewAs, roles: [viewAs] }
      : realUserProfile;

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      // A synchronous hint for the pre-paint script in layout.tsx (Firebase
      // keeps the session in IndexedDB): visitors and signed-in people get
      // different default layouts, decided before the first paint.
      try { if (currentUser) localStorage.setItem('bb-signed-in', '1'); else localStorage.removeItem('bb-signed-in'); } catch { /* private mode */ }
      document.documentElement.dataset.signedIn = currentUser ? '1' : '0';
      if (!currentUser) {
        setRealUserProfile(null);
        // Clear any active impersonation on sign-out
        setViewAsState(null);
        try { sessionStorage.removeItem('bb-view-as'); } catch { /* ignore */ }
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;

    if (user) {
      setLoading(true);
      const userDocRef = doc(db, 'users', user.uid);
      unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const profile = docSnap.data() as UserProfile;
          setRealUserProfile(profile);
          // Apply the saved language on a fresh browser (cross-device sign-in):
          // if the profile has a preferredLocale but this browser has no
          // NEXT_LOCALE cookie yet, set it and reload once so the UI comes up in
          // the user's language.
          //
          // ⚠️ LOOP-PROOF RULES (this reload was crashing iOS):
          // On iOS/WKWebView (Safari PWA AND Chrome-iOS — same engine), a
          // document.cookie write can silently fail to persist. The old code
          // reloaded unconditionally after writing, so on such devices every
          // boot repeated: profile arrives (~5-7s in) → cookie still missing →
          // set + reload → forever, until iOS kills the page with "A problem
          // repeatedly occurred". Now we (a) verify the cookie actually stuck
          // before reloading — if the write didn't take, reloading can't help
          // and the server-side geo default stays in charge; and (b) attempt
          // this at most once per session, and never when sessionStorage is
          // unavailable to track that.
          if (!docSnap.metadata.fromCache) {
            const preferred = (profile as any).preferredLocale as string | undefined;
            if (preferred && typeof document !== 'undefined' && !/(?:^|;\s*)NEXT_LOCALE=/.test(document.cookie)) {
              let allowed = false;
              try {
                if (!sessionStorage.getItem('bb-locale-reload-done')) {
                  sessionStorage.setItem('bb-locale-reload-done', '1');
                  allowed = true;
                }
              } catch { /* can't track attempts → never auto-reload */ }
              document.cookie = `NEXT_LOCALE=${preferred}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
              const stuck = /(?:^|;\s*)NEXT_LOCALE=/.test(document.cookie);
              if (allowed && stuck) {
                try { (window as any).__bbSend?.('reload-locale', { preferred }); } catch { /* ignore */ }
                window.location.reload();
                return;
              }
            }
          }
          // Only act on server-confirmed data — skip stale local cache to avoid
          // triggering redirects before the real auth state is known.
          if (!docSnap.metadata.fromCache) {
            const currentPathname = pathnameRef.current;
            if (!profile.onboardingComplete) {
              // New users must complete onboarding first
              if (currentPathname !== '/onboarding') {
                router.push('/onboarding');
              }
            }
            // Redirect away from /signin is handled in signin/page.tsx so it can
            // also restore the page the user was on before being sent to sign-in.
          }
        } else {
          // User exists in Auth but not Firestore (new user — signin page handles profile creation)
          setRealUserProfile(null);
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching user profile:", error);
        setRealUserProfile(null);
        setLoading(false);
      });
    } else {
      // No user, not loading
      setLoading(false);
    }

    return () => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  // pathname intentionally NOT in deps — we use pathnameRef so the snapshot
  // listener is not torn down and recreated on every navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, router]);

  const signOut = async () => {
    try {
      setViewAsState(null);
      try { sessionStorage.removeItem('bb-view-as'); } catch { /* ignore */ }
      await firebaseSignOut(auth);
      router.replace('/signin');
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    realUserProfile,
    loading,
    signOut,
    viewAs,
    setViewAs,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
